import React, {useState} from "react";
import {FiInfo, FiChevronDown} from "react-icons/fi";
import {allowedActions, COMMAND, usesLegacyOpen, turnstileTypeLabel, entryTypeLabel, COMMAND_STATE_LABEL, COMMAND_LABEL} from "./mapConstants";
import {hasCommandForm} from "./commandForms";
import CommandFormModal from "./CommandFormModal";

// Sladěno s Material paletou editoru mapy (Tailwind v1 tokeny neexistují → inline).
const C = {
    primary: "#004ac6",
    surface: "#faf8ff",
    surfaceLow: "#f3f3fe",
    surfaceLowest: "#ffffff",
    outlineVariant: "#c3c6d7",
    outline: "#737686",
    onSurface: "#191b23",
    onSurfaceVariant: "#434655",
    online: "#22c55e",
    offline: "#94a3b8",
    error: "#ba1a1a",
};

// Dotazovací příkazy nesou data v ACKu — v historii je jde rozbalit.
const QUERY_COMMANDS = [COMMAND.STATUS, COMMAND.DEVICE_INFO];

// response je JSON string z DB; když nejde naparsovat, ukaž ho tak jak je.
const formatResponse = (raw) => {
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch (e) {
        return String(raw);
    }
};

const stateColor = (state) => {
    if (state === "SUCCESS") return "#16a34a";
    if (state === "ERROR") return "#dc2626";
    if (state === "PENDING") return "#d97706";
    return C.outline;
};

const sectionLabel = {
    fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: C.outline,
    fontWeight: 600, margin: "0 0 8px", display: "block",
};

const legendTitle = {
    fontSize: 10, fontWeight: 700, color: C.outline, textTransform: "uppercase",
    letterSpacing: "0.06em", margin: "0 0 4px",
};

// Co která akce dělá — zobrazuje se jen pro akce, co má daný turniket reálně dostupné.
const ACTION_DESCRIPTION = {
    [COMMAND.OPEN]: "Otevře průchod pro jeden vstup.",
    [COMMAND.BLOCK]: "Trvale zablokuje nebo odblokuje průchod (latched — přežije restart desky).",
    [COMMAND.REBOOT]: "Restartuje Android zařízení.",
    [COMMAND.DISPLAY]: "Zobrazí text na displeji zařízení.",
    [COMMAND.STATUS]: "Dotáže se na aktuální stav řídicí desky.",
    [COMMAND.DEVICE_INFO]: "Načte informace o zařízení (HW/SW, síť, baterie).",
    [COMMAND.SYNC_NOW]: "Vynutí okamžitou synchronizaci dat se serverem.",
    [COMMAND.RESTART_BOARD]: "Restartuje řídicí desku turniketu.",
    [COMMAND.RESET_COUNT]: "Vynuluje počítadla průchodů na desce — nevratné.",
    [COMMAND.SET_TIME]: "Nastaví čas na desce (bez zadání = čas serveru).",
    [COMMAND.FREE_PASSAGE]: "Nastaví volný / trvale otevřený průchod bez ověřování.",
    [COMMAND.READERS]: "Zapne nebo vypne QR a NFC čtečky.",
    [COMMAND.LED]: "Rozsvítí LED v požadované barvě a délce.",
    [COMMAND.SEMAPHORE]: "Ovládá externí semafor (svit / blikání).",
    [COMMAND.UI]: "Zobrazí nebo skryje status bar a navigation bar zařízení.",
    [COMMAND.SHUTDOWN]: "Vypne zařízení — samo se nevrátí online.",
};

// Legenda popisující, co jednotlivá tlačítka dělají — stejný sbalitelný vzor jako na /turnstile.
const ActionLegend = ({actions}) => {
    const [open, setOpen] = useState(false);
    return (
        <div style={{marginBottom: 8}}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 12, color: C.outline, padding: "2px 0", fontWeight: 500,
                }}
            >
                <FiInfo size={12}/>
                Legenda
                <FiChevronDown size={11} style={{transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s"}}/>
            </button>

            {open && (
                <div style={{
                    marginTop: 6, border: `1px solid ${C.outlineVariant}`, borderRadius: 8,
                    background: C.surfaceLowest, padding: "10px 12px",
                }}>
                    <p style={legendTitle}>Co která akce dělá</p>
                    <div style={{display: "flex", flexDirection: "column", gap: 5}}>
                        {actions.map((code) => (
                            <div key={code} style={{display: "flex", gap: 6, fontSize: 12}}>
                                <span style={{color: C.onSurface, fontWeight: 600, flexShrink: 0, minWidth: 110}}>
                                    {COMMAND_LABEL[code]}
                                </span>
                                <span style={{color: C.onSurfaceVariant}}>
                                    {ACTION_DESCRIPTION[code] || "—"}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const InfoRow = ({label, value}) => (
    <div style={{display: "flex", justifyContent: "space-between", gap: 8, padding: "3px 0"}}>
        <span style={{color: C.onSurfaceVariant}}>{label}</span>
        <span style={{color: C.onSurface, textAlign: "right", wordBreak: "break-all", fontWeight: 500}}>
            {value == null || value === "" ? "—" : value}
        </span>
    </div>
);

const TurnstileActionPanel = ({turnstile, unknownId, onAction, onDetail, commands, zoneName, bg = C.surface}) => {
    const [formCommand, setFormCommand] = useState(null);

    if (!turnstile) {
        return (
            <div style={{padding: 16, fontSize: "var(--enigoo-fs-sm)", color: C.onSurfaceVariant}}>
                <p style={{margin: 0}}>Turniket (id {unknownId}) je neznámý nebo byl smazán. Akce nejsou dostupné.</p>
            </div>
        );
    }
    const actions = allowedActions(turnstile.turnstileType);
    const online = turnstile.connected === 1;
    const zone = zoneName || (turnstile.zone != null ? ("Zóna #" + turnstile.zone) : null);
    const recent = Array.isArray(commands) ? commands.slice(0, 5) : null;

    // Legacy typy (Elvis/DSN) posílají Otevřít přes starý GET /open bez payloadu —
    // formulář pro ně nedává smysl, ať má COMMAND.OPEN formulář nebo ne.
    const legacyOpen = usesLegacyOpen(turnstile.turnstileType);
    const handleActionClick = (code) => {
        const showForm = hasCommandForm(code) && !(code === COMMAND.OPEN && legacyOpen);
        if (showForm) { setFormCommand(code); return; }
        onAction(code, {});
    };

    return (
        <div style={{display: "flex", flexDirection: "column", height: "100%", background: bg, fontSize: "var(--enigoo-fs-sm)"}}>
            {/* header + online badge */}
            <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                padding: 16, borderBottom: `1px solid ${C.outlineVariant}`,
            }}>
                <h3 style={{margin: 0, fontSize: 16, fontWeight: 600, color: C.onSurface}}>
                    {turnstile.description || ("Turniket #" + turnstile.id)}
                </h3>
                <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
                    padding: "3px 10px", borderRadius: 999, color: "#fff",
                    background: online ? C.online : C.offline, whiteSpace: "nowrap",
                }}>
                    <span style={{width: 7, height: 7, borderRadius: 999, background: "#fff"}}/>
                    {online ? "Online" : "Offline"}
                </span>
            </div>

            <div style={{flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 20}}>
                {/* info grid */}
                <section>
                    <label style={sectionLabel}>Informace</label>
                    <div style={{
                        border: `1px solid ${C.outlineVariant}`, borderRadius: 12,
                        padding: "10px 12px", background: C.surfaceLowest,
                    }}>
                        <InfoRow label="Typ zařízení" value={turnstileTypeLabel(turnstile.turnstileType)}/>
                        <InfoRow label="Směr" value={entryTypeLabel(turnstile.type)}/>
                        <InfoRow label="Zóna" value={zone}/>
                        {turnstile.uid && <InfoRow label="UID" value={turnstile.uid}/>}
                        <InfoRow label="IP" value={turnstile.ipAddress}/>
                        <InfoRow label="MAC" value={turnstile.macAddress}/>
                        <InfoRow label="Poslední akce" value={turnstile.lastAction}/>
                    </div>
                </section>

                {onDetail && (
                    <button type="button" onClick={onDetail} style={{
                        width: "100%", padding: "9px 12px", borderRadius: 8, cursor: "pointer",
                        border: `1px solid ${C.primary}`, background: "transparent", color: C.primary,
                        fontSize: 13, fontWeight: 600,
                    }}>
                        Detail turniketu
                    </button>
                )}

                {/* akce */}
                <section>
                    <label style={sectionLabel}>Akce</label>
                    {actions.length > 0 && <ActionLegend actions={actions}/>}
                    {actions.length === 0 ? (
                        <span style={{color: C.onSurfaceVariant}}>Žádné dostupné akce pro tento typ.</span>
                    ) : (
                        <div style={{display: "flex", flexWrap: "wrap", gap: 8}}>
                            {actions.map((code) => (
                                <button key={code} type="button" onClick={() => handleActionClick(code)} style={{
                                    padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                                    background: C.primary, color: "#fff", fontSize: 13, fontWeight: 600,
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                                }}>
                                    {COMMAND_LABEL[code]}
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                {/* historie příkazů (jen socket validatory ji mají) */}
                {recent && (
                    <section>
                        <label style={sectionLabel}>Historie příkazů</label>
                        {recent.length === 0 && <p style={{margin: 0, color: C.onSurfaceVariant}}>Zatím žádné příkazy.</p>}
                        {recent.map((c, i) => (
                            <div key={c.correlationId || c.id || i}
                                 style={{padding: "6px 0", borderTop: i ? `1px solid ${C.outlineVariant}` : "none"}}>
                                <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8}}
                                     title={c.errorDetail || ""}>
                                    <span style={{color: C.onSurface}}>{COMMAND_LABEL[c.type] || c.type}</span>
                                    <span style={{display: "flex", alignItems: "center", gap: 8}}>
                                        <span style={{color: C.onSurfaceVariant, fontSize: 12}}>{c.dateCreate || ""}</span>
                                        <span style={{
                                            fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 999,
                                            color: "#fff", background: stateColor(c.state),
                                        }}>
                                            {COMMAND_STATE_LABEL[c.state] || c.state || "—"}
                                        </span>
                                    </span>
                                </div>
                                {/* status / device_info vracejí data v ACKu — bez tohohle by nebyla k ničemu */}
                                {QUERY_COMMANDS.includes(c.type) && c.state === "SUCCESS" && c.response && (
                                    <details style={{marginTop: 4}}>
                                        <summary style={{cursor: "pointer", fontSize: 12, color: C.primary}}>
                                            Odpověď zařízení
                                        </summary>
                                        <pre style={{
                                            margin: "4px 0 0", padding: 8, borderRadius: 8, fontSize: 11,
                                            background: C.surfaceLow, color: C.onSurfaceVariant,
                                            whiteSpace: "pre-wrap", wordBreak: "break-all",
                                        }}>{formatResponse(c.response)}</pre>
                                    </details>
                                )}
                            </div>
                        ))}
                    </section>
                )}
            </div>

            <CommandFormModal
                command={formCommand}
                turnstile={turnstile}
                onClose={() => setFormCommand(null)}
                sendCommand={onAction}
            />
        </div>
    );
};

export default TurnstileActionPanel;
